import { useState, useEffect, useMemo } from "react";
import { DataTable, DataTableColumn } from "mantine-datatable";
import { StatCard } from "../../components/stat-card";
import { ReportHeader, ExportButton, RefreshButton } from "../../components/report-header";
import { ReportPanel, PanelTabs } from "../../components/report-panel";
import { DateRange } from "../../types";
import { BarChart } from "../../../../components/custom/charts/bar-chart";
import { formatCurrencyDecimal } from "../../../../utils/helpers";
import { ReportService } from "../../services/report-service";

// ==================== HELPERS ====================

const today = new Date();
const formatDate = (d: Date) => d.toISOString().split("T")[0]; // YYYY-MM-DD

const getRankBadge = (rank: number) => {
  if (rank === 1) return <span className="text-2xl">🥇</span>;
  if (rank === 2) return <span className="text-2xl">🥈</span>;
  if (rank === 3) return <span className="text-2xl">🥉</span>;
  return <span className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-sm font-bold text-gray-600">{rank}</span>;
};

// ==================== COMPONENT ====================

// Helper to compute date range from tab
const getDateRangeFromTab = (tab: string): DateRange => {
  const now = new Date();
  switch (tab) {
    case "week": {
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay()); // Sunday
      return { from: startOfWeek, to: now };
    }
    case "month": {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      return { from: startOfMonth, to: now };
    }
    case "today":
    default:
      return { from: now, to: now };
  }
};

export const SalesDashboard = () => {
  const [viewTab, setViewTab] = useState("today");
  const [dateRange, setDateRange] = useState<DateRange>(getDateRangeFromTab("today"));

  // --- API hooks ---
  const {
    fetch: fetchSummary,
    response: summaryResponse,
    loading: summaryLoading,
  } = ReportService.summary();

  const {
    fetch: fetchEmployeeRevenue,
    response: employeeResponse,
    loading: employeeLoading,
  } = ReportService.revenueByEmployee();

  const {
    fetch: fetchHourlyRevenue,
    response: hourlyResponse,
    loading: hourlyLoading,
  } = ReportService.revenueByHour();

  const {
    fetch: fetchServiceRevenue,
    response: serviceResponse,
    loading: serviceLoading,
  } = ReportService.revenueByService();

  // --- Derived data ---
  const summaryData = summaryResponse?.data;
  const employeeData = employeeResponse?.data ?? [];
  const hourlyData = hourlyResponse?.data ?? [];
  const serviceData = serviceResponse?.data ?? [];

  const isLoading = summaryLoading || employeeLoading || hourlyLoading || serviceLoading;

  // --- Fetch all data ---
  const fetchAllData = () => {
    const params = {
      from_date: formatDate(dateRange.from),
      to_date: formatDate(dateRange.to),
    };
    fetchSummary(params);
    fetchEmployeeRevenue(params);
    fetchHourlyRevenue(params);
    fetchServiceRevenue(params);
  };

  // Update dateRange when tab changes
  useEffect(() => {
    setDateRange(getDateRangeFromTab(viewTab));
  }, [viewTab]);

  // Initial fetch + refetch on date range change
  useEffect(() => {
    fetchAllData();
  }, [dateRange.from?.toISOString(), dateRange.to?.toISOString()]);

  const handleRefresh = () => {
    fetchAllData();
  };

  const handleExport = () => {
    console.log("Exporting sales report...");
  };

  // --- Computed values for display ---
  const totalRevenue = summaryData?.total_revenue ?? 0;
  const totalOrders = summaryData?.total_orders ?? 0;
  const avgOrderValue = summaryData?.avg_order_value ?? 0;
  const completedOrders = summaryData?.completed_orders ?? 0;
  const cancelledOrders = summaryData?.cancelled_orders ?? 0;

  // Sort employees by revenue descending
  const rankedEmployees = useMemo(() => {
    return [...employeeData]
      .sort((a, b) => b.revenue - a.revenue)
      .map((emp, index) => ({
        ...emp,
        rank: index + 1,
      }));
  }, [employeeData]);

  // Sort services by revenue descending
  const rankedServices = useMemo(() => {
    return [...serviceData].sort((a, b) => b.revenue - a.revenue);
  }, [serviceData]);

  // Sort hourly data by hour
  const sortedHourlyData = useMemo(() => {
    return [...hourlyData].sort((a, b) => a.hour - b.hour);
  }, [hourlyData]);

  // Find peak and low hours
  const peakHour = useMemo(() => {
    if (sortedHourlyData.length === 0) return null;
    return sortedHourlyData.reduce((max, d) => (d.revenue > max.revenue ? d : max), sortedHourlyData[0]);
  }, [sortedHourlyData]);

  const lowHour = useMemo(() => {
    if (sortedHourlyData.length === 0) return null;
    return sortedHourlyData.reduce((min, d) => (d.revenue < min.revenue ? d : min), sortedHourlyData[0]);
  }, [sortedHourlyData]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <ReportHeader
        title="Báo cáo Doanh số"
        subtitle="Theo dõi doanh số và hiệu suất bán hàng"
        icon={
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        }
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        actions={
          <>
            <RefreshButton onClick={handleRefresh} loading={isLoading} />
            <ExportButton onClick={handleExport} />
          </>
        }
      >
        {/* Tabs */}
        <div className="flex items-center gap-2">
          <PanelTabs
            tabs={[
              { key: "today", label: "Hôm nay" },
              { key: "week", label: "Tuần này" },
              { key: "month", label: "Tháng này" },
            ]}
            activeTab={viewTab}
            onChange={setViewTab}
          />
        </div>
      </ReportHeader>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          title="Doanh số hôm nay"
          value={totalRevenue}
          suffix=" ₫"
          color="green"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
          loading={summaryLoading}
        />
        <StatCard
          title="Số đơn hôm nay"
          value={totalOrders}
          color="blue"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          }
          loading={summaryLoading}
        />
        <StatCard
          title="Giá trị TB/đơn"
          value={avgOrderValue}
          suffix=" ₫"
          color="orange"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
          loading={summaryLoading}
        />
        <StatCard
          title="Đơn hoàn thành"
          value={completedOrders}
          color="purple"
          icon={
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          }
          loading={summaryLoading}
        />
      </div>

      {/* Summary Stats Bar */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Tổng quan đơn hàng</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              Tổng doanh thu: {formatCurrencyDecimal(totalRevenue)} ₫
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-emerald-600">{completedOrders}/{totalOrders}</p>
            <p className="text-xs text-gray-500">đơn hoàn thành</p>
          </div>
        </div>
        <div className="w-full h-4 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-1000"
            style={{ width: `${totalOrders > 0 ? Math.round((completedOrders / totalOrders) * 100) : 0}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>0%</span>
          <span>25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Hourly Sales Chart */}
        <ReportPanel
          title="Doanh số theo giờ"
          subtitle="Phân bổ doanh số trong ngày"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        >
          <div className="h-72">
            <BarChart
              chartData={[
                {
                  name: "Doanh thu",
                  data: sortedHourlyData.map((d) => d.revenue),
                },
              ]}
              chartCategories={sortedHourlyData.map((d) => `${d.hour}h`)}
              chartColors={["#10b981"]}
              chartHeight={260}
              chartLoading={hourlyLoading}
            />
          </div>
          <div className="mt-4 flex items-center gap-4 text-sm">
            {peakHour && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-emerald-500 rounded-full" />
                <span className="text-gray-600">Peak: {peakHour.hour}h</span>
              </div>
            )}
            {lowHour && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-gray-300 rounded-full" />
                <span className="text-gray-600">Low: {lowHour.hour}h</span>
              </div>
            )}
          </div>
        </ReportPanel>

        {/* Orders by Service */}
        <ReportPanel
          title="Doanh số theo dịch vụ"
          subtitle="Top dịch vụ hôm nay"
          icon={
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
            </svg>
          }
        >
          {serviceLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
            </div>
          ) : rankedServices.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              Không có dữ liệu
            </div>
          ) : (
            <div className="space-y-4">
              {rankedServices.map((service, index) => (
                <div key={service.item_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="w-8 h-8 bg-emerald-100 text-emerald-600 rounded-lg flex items-center justify-center text-sm font-bold">
                      {index + 1}
                    </span>
                    <div>
                      <p className="font-medium text-gray-900">Dịch vụ #{service.item_id}</p>
                      <p className="text-xs text-gray-500">{service.order_count} đơn</p>
                    </div>
                  </div>
                  <p className="font-bold text-gray-900">{formatCurrencyDecimal(service.revenue)} ₫</p>
                </div>
              ))}
            </div>
          )}
        </ReportPanel>
      </div>

      {/* Employee Performance Table */}
      <ReportPanel
        title="Bảng xếp hạng nhân viên"
        subtitle="Top performers theo doanh thu"
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        }
        noPadding
      >
        <div className="datatables">
          <DataTable
            fetching={employeeLoading}
            highlightOnHover
            withColumnBorders
            noRecordsText="Không có dữ liệu"
            className="whitespace-nowrap table-hover"
            records={rankedEmployees}
            minHeight={200}
            columns={[
              {
                accessor: "rank",
                title: "#",
                textAlignment: "center",
                width: 60,
                render: (row) => getRankBadge(row.rank),
              },
              {
                accessor: "employee_id",
                title: "Nhân viên",
                render: (row) => (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center text-white font-bold">
                      {row.employee_id}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Nhân viên #{row.employee_id}</p>
                      <p className="text-xs text-gray-500">Nhân viên bán hàng</p>
                    </div>
                  </div>
                ),
              },
              {
                accessor: "revenue",
                title: "Doanh số",
                textAlignment: "right",
                render: (row) => <span className="font-bold text-gray-900">{formatCurrencyDecimal(row.revenue)} ₫</span>,
              },
              {
                accessor: "order_count",
                title: "Số đơn",
                textAlignment: "right",
                render: (row) => <span className="font-medium text-gray-700">{row.order_count}</span>,
              },
              {
                accessor: "avg_value",
                title: "TB/đơn",
                textAlignment: "right",
                render: (row) => <span className="text-gray-600">{formatCurrencyDecimal(row.avg_value)} ₫</span>,
              },
            ] as DataTableColumn<any>[]}
          />
        </div>
      </ReportPanel>
    </div>
  );
};

export default SalesDashboard;
